import { useCallback, useEffect, useRef } from "react";
import { useBlocker, useLocation } from "react-router-dom";
import type { Location } from "react-router-dom";
import { useAppDispatch, useAppSelector, useAppStore } from "@/app/hooks";
import { legacyAction } from "@/app/legacySlice";
import { markSaved } from "@/features/configuration/slice";
import { resolveRoute } from "@/app/routes";
import type { RootState } from "@/app/store";
import type { Shop } from "@/shared/types/shop";
import { applyShop, discardConfiguration, navigationDelegated, shopSwitchDelegated } from "./actions";
import {
  selectDisplayedShops,
  selectEditorHasChanges,
  selectHasUnsavedConfiguration,
  selectHistoryChanges,
  selectPendingShop,
  selectSelectedEditors,
  selectShowSaveConfigModal,
} from "./legacySelectors";

const setSelectedCampaign = legacyAction<unknown>("setSelectedCampaign");
const setSelectedBanner = legacyAction<unknown>("setSelectedBanner");
const setShowSaveConfigModal = legacyAction<boolean>("setShowSaveConfigModal");
const setPendingWebsite = legacyAction<Shop | null>("setPendingWebsite");

type Decision = "proceed" | "proceed-clear" | "ask" | "delegate";

/**
 * Stessi criteri di `navigationIsBlocked` (legacy): valutati sullo stato CORRENTE dello store.
 * Nel wizard il ramo della history viene saltato del tutto (come il legacy: la history non si azzera
 * mai li'), quindi "proceed-clear" (azzera la history) si distingue da "proceed" semplice (fuori wizard,
 * senza differenze reali E fuori wizard, oppure dentro al wizard senza editor modificato).
 * Diversamente dal legacy l'editor modificato ha la precedenza sulla history (come gia' in `switchShop`):
 * prima chiede l'editor, poi, alla conferma, la navigazione riparte e la guardia chiede della history.
 */
function decide(state: RootState, currentPathname: string): Decision {
  const editor = selectEditorHasChanges(state);
  if (editor.campaign || editor.banner) return "delegate";
  if (selectHistoryChanges(state).length > 0 && resolveRoute(currentPathname).group !== "wizard") {
    return selectHasUnsavedConfiguration(state) ? "ask" : "proceed-clear";
  }
  return "proceed";
}

/**
 * Guardia delle modifiche non salvate sul data router. Sostituisce `navigationIsBlocked` + `switchWebsite`
 * + il listener `change-website` di App.jsx. La modale e' pilotata dal flag legacy `showSaveConfigModal`
 * perche' anche le modali notifiche (ancora legacy, fase 1b) la aprono.
 */
export function useUnsavedChanges() {
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const modalOpen = useAppSelector(selectShowSaveConfigModal);
  const pendingShop = useAppSelector(selectPendingShop);
  const editorChanges = useAppSelector(selectEditorHasChanges);
  const historyLength = useAppSelector(selectHistoryChanges).length;
  // Finche' un blocco e' in attesa la rotta corrente non cambia: location.pathname e' quella di partenza.
  const pathname = location.pathname;

  // Blocca solo quando c'e' un motivo potenziale: la decisione fine la prende l'effetto sotto sullo stato fresco.
  // Mai verso le rotte pubbliche: logout e sessione scaduta (401/403) portano a "/" senza chiedere, come il legacy.
  const blocker = useBlocker(({ nextLocation }) => {
    const state = store.getState();
    return (
      resolveRoute(nextLocation.pathname).group !== "home" &&
      (selectHistoryChanges(state).length > 0 ||
        selectEditorHasChanges(state).campaign ||
        selectEditorHasChanges(state).banner)
    );
  });

  // Marca la `location` del blocco gia' risolto (proceed/reset), cosi' un solo effetto puo' essere
  // rivalutato piu' volte per lo stesso blocco (perche' dipende anche da editorChanges/historyLength/
  // modalOpen, non solo da `blocker`) senza richiamare `proceed`/`reset` due volte sulla stessa
  // transizione. Scritta solo dentro effetti/handler, mai durante il render.
  const settled = useRef<Location | null>(null);

  useEffect(() => {
    if (blocker.state !== "blocked") {
      settled.current = null;
      return;
    }
    if (settled.current === blocker.location) return;
    const state = store.getState();
    const decision = decide(state, pathname);
    if (decision === "proceed" || decision === "proceed-clear") {
      settled.current = blocker.location;
      if (decision === "proceed-clear") dispatch(markSaved());
      blocker.proceed();
      return;
    }
    if (decision === "delegate") {
      settled.current = blocker.location;
      dispatch(navigationDelegated(resolveRoute(blocker.location.pathname).name));
      blocker.reset();
      return;
    }
    // "ask": la modale resta (o si apre) in attesa dell'utente. Non si marca `settled` perche' un
    // cambio di stato successivo (es. l'editor legacy si chiude) deve poter far rivalutare la stessa
    // `location` verso "proceed" mentre la modale non e' ancora aperta.
    if (!modalOpen) dispatch(setShowSaveConfigModal(true));
  }, [blocker, modalOpen, editorChanges, historyLength, pathname, dispatch, store]);

  // Editor aperto ma intatto: si chiude a ogni navigazione (come navigationIsBlocked prima di lasciar passare),
  // anche verso la stessa rotta: il click sulla tab corrente (es. "Campaigns" nell'Header legacy) torna alla
  // lista. Per questo dipende da `location.key` (nuovo a ogni navigazione, anche stesso path/search/hash) e
  // non dal pathname. Legge lo store direttamente cosi' l'effetto dipende solo dalla navigazione.
  const locationKey = location.key;
  useEffect(() => {
    const state = store.getState();
    const editors = selectSelectedEditors(state);
    const changes = selectEditorHasChanges(state);
    if (editors.campaign && !changes.campaign) dispatch(setSelectedCampaign(null));
    if (editors.banner && !changes.banner) dispatch(setSelectedBanner(null));
  }, [locationKey, dispatch, store]);

  const switchShop = useCallback(
    (shop: Shop) => {
      const state = store.getState();
      const editor = selectEditorHasChanges(state);
      if (editor.campaign || editor.banner) {
        dispatch(shopSwitchDelegated(shop));
        return;
      }
      if (selectHistoryChanges(state).length > 0 && selectHasUnsavedConfiguration(state)) {
        dispatch(setShowSaveConfigModal(true));
        dispatch(setPendingWebsite(shop));
        return;
      }
      dispatch(applyShop(shop));
    },
    [dispatch, store],
  );

  const cancelExit = useCallback(() => {
    dispatch(setShowSaveConfigModal(false));
    if (blocker.state === "blocked") {
      settled.current = blocker.location;
      blocker.reset();
    }
  }, [blocker, dispatch]);

  const exitWithoutSaving = useCallback(() => {
    dispatch(discardConfiguration());
    dispatch(setShowSaveConfigModal(false));
    if (blocker.state === "blocked") {
      settled.current = blocker.location;
      blocker.proceed();
    }
    // Come `ChangesModal.exitWithoutSave` legacy: una navigazione bloccata e uno shop in attesa possono
    // essere risolti nella stessa chiamata (es. la modale si e' riaperta su una seconda navigazione mentre
    // c'era gia' uno shop in attesa dal picker).
    if (pendingShop) {
      dispatch(applyShop(pendingShopFromList(store.getState(), pendingShop)));
      dispatch(setPendingWebsite(null));
    }
  }, [blocker, dispatch, pendingShop, store]);

  return { switchShop, modalOpen, cancelExit, exitWithoutSaving };
}

/** Come il listener `change-website` legacy: usa la versione in lista dello shop in attesa, se c'e'. */
function pendingShopFromList(state: RootState, pending: Shop): Shop {
  return selectDisplayedShops(state).find((item) => item.id === pending.id) ?? pending;
}
